import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '../../utils/formatters';

export const GlassButton = ({
  children,
  className,
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled = false,
  icon: Icon,
  iconPosition = 'left',
  fullWidth = false,
  animate = true,
  ...props
}) => {
  const variants = {
    primary: 'bg-gradient-to-r from-accent-primary to-accent-secondary text-white shadow-glass hover:shadow-glow',
    secondary: 'bg-white/10 text-white/90 border border-white/20 hover:bg-white/20 hover:border-white/30',
    ghost: 'bg-transparent text-white/70 hover:bg-white/5 hover:text-white',
    danger: 'bg-gradient-to-r from-red-500 to-red-600 text-white hover:shadow-glow-danger',
    success: 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:shadow-glow-success',
    outline: 'bg-transparent border border-white/20 text-white/80 hover:bg-white/5 hover:border-white/40',
    glass: 'glass text-white/90 hover:bg-white/10',
  };

  const sizes = {
    xs: 'px-3 py-1.5 text-xs',
    sm: 'px-4 py-2 text-sm',
    md: 'px-5 py-2.5 text-sm',
    lg: 'px-6 py-3 text-base',
    xl: 'px-8 py-4 text-lg',
    icon: 'p-2.5',
  };

  const baseClasses = cn(
    'relative inline-flex items-center justify-center gap-2 rounded-xl font-medium',
    'transition-all duration-300 ease-out',
    'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:transform-none',
    'focus:outline-none focus:ring-2 focus:ring-accent-primary/50',
    variants[variant],
    sizes[size],
    fullWidth && 'w-full',
    className
  );

  const content = (
    <>
      {loading && (
        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      )}
      {!loading && Icon && iconPosition === 'left' && <Icon className="w-4 h-4" />}
      {children}
      {!loading && Icon && iconPosition === 'right' && <Icon className="w-4 h-4" />}
    </>
  );

  if (animate) {
    return (
      <motion.button
        whileHover={{ scale: disabled || loading ? 1 : 1.02 }}
        whileTap={{ scale: disabled || loading ? 1 : 0.98 }}
        className={baseClasses}
        disabled={disabled || loading}
        {...props}
      >
        {content}
      </motion.button>
    );
  }

  return (
    <button className={baseClasses} disabled={disabled || loading} {...props}>
      {content}
    </button>
  );
};

export const GlassButtonGroup = ({ children, className, vertical = false }) => (
  <div className={cn(
    'flex',
    vertical ? 'flex-col space-y-1' : 'flex-row space-x-1',
    className
  )}>
    {children}
  </div>
);

export default GlassButton;
